import { useEffect, useMemo, useState } from 'react';
import { Text, TouchableOpacity, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import TaskCard from '@/components/Tasks/TaskCard';
import Unicon from '@/components/General/Unicon';

export const getTaskInstanceDate = taskInstance => taskInstance?.dueDate || taskInstance?.EventInstance?.startTime || taskInstance?.time;

export const getActiveAssigneeCount = taskInstance => taskInstance?.Assignees?.filter(assignee => assignee?.assignmentResponse !== 'DECLINED').length || 0;

export const getCoverageNeed = taskInstance => taskInstance?.VolunteerTeam?.assigneesNeeded || taskInstance?.VolunteerTeam?.VolunteerTeamTasks?.find(item => item.taskID === taskInstance?.taskID)?.assigneesNeeded || 1;

export const hasCoverageGap = taskInstance => getActiveAssigneeCount(taskInstance) < getCoverageNeed(taskInstance);

const formatDateKey = value => {
    if (!value) return 'Unscheduled';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Unscheduled';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatDateLabel = key => {
    if (key === 'Unscheduled') return key;
    const [year, month, day] = key.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const today = new Date();
    const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
    const dateKey = date.toDateString();
    if (dateKey === today.toDateString()) return 'Today';
    if (dateKey === tomorrow.toDateString()) return 'Tomorrow';
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const INITIAL_VISIBLE_GROUPS = 5;
const LOAD_MORE_GROUPS = 5;

export default function CoverageBoard({ taskInstances = [], emptyMessage = 'No managed coverage in the next 30 days.', fromScreen = null }) {
    const [gapsOnlyGroups, setGapsOnlyGroups] = useState({});
    const [visibleGroupCount, setVisibleGroupCount] = useState(INITIAL_VISIBLE_GROUPS);
    const groups = useMemo(() => {
        const sorted = [...taskInstances].sort((left, right) => {
            const leftGap = hasCoverageGap(left);
            const rightGap = hasCoverageGap(right);
            const dateDifference = new Date(getTaskInstanceDate(left) || 0) - new Date(getTaskInstanceDate(right) || 0);
            if (formatDateKey(getTaskInstanceDate(left)) === formatDateKey(getTaskInstanceDate(right)) && leftGap !== rightGap) return leftGap ? -1 : 1;
            return dateDifference;
        });
        return sorted.reduce((result, taskInstance) => {
            const key = formatDateKey(getTaskInstanceDate(taskInstance));
            const existing = result.find(group => group.key === key);
            if (existing) existing.items.push(taskInstance);
            else result.push({ key, items: [taskInstance] });
            return result;
        }, []);
    }, [taskInstances]);

    // Reset pagination when data changes
    useEffect(() => { setVisibleGroupCount(INITIAL_VISIBLE_GROUPS); }, [taskInstances]);

    if (groups.length === 0) {
        return (
            <View style={styles.empty}>
                <View style={styles.emptyIcon}><Unicon name="calendar-outline" size={22} colorVariant="tasks" /></View>
                <Text style={styles.emptyTitle}>Coverage is clear</Text>
                <Text style={styles.emptyText}>{emptyMessage}</Text>
            </View>
        );
    }

    const visibleGroups = groups.slice(0, visibleGroupCount);
    const hiddenGroupCount = groups.length - visibleGroupCount;
    const hiddenTaskCount = groups.slice(visibleGroupCount).reduce((sum, g) => sum + g.items.length, 0);

    return (
        <View style={styles.board}>
            {visibleGroups.map(group => {
                const gapCount = group.items.filter(hasCoverageGap).length;
                const gapsOnly = !!gapsOnlyGroups[group.key];
                const visibleItems = gapsOnly ? group.items.filter(hasCoverageGap) : group.items;
                return (
                    <View key={group.key} style={styles.group}>
                        <View style={styles.dateRail}>
                            <View style={styles.dateMarker(gapCount > 0)} />
                            <View style={styles.railLine} />
                        </View>
                        <View style={styles.groupContent}>
                            <View style={styles.groupHeader}>
                                <Text style={styles.dateLabel}>{formatDateLabel(group.key)}</Text>
                                {gapCount > 0 && (
                                    <View style={styles.groupHeaderRight}>
                                        <Text style={styles.dateMeta}>{gapCount} gap{gapCount === 1 ? '' : 's'}</Text>
                                        <TouchableOpacity
                                            testID={`CoverageBoardToggle-${group.key}`}
                                            accessibilityRole="button"
                                            accessibilityLabel={gapsOnly ? `Show all tasks for ${formatDateLabel(group.key)}` : `Show only gaps for ${formatDateLabel(group.key)}`}
                                            onPress={() => setGapsOnlyGroups(prev => ({ ...prev, [group.key]: !prev[group.key] }))}
                                            style={styles.gapToggle(gapsOnly)}
                                        >
                                            <Text style={styles.gapToggleText(gapsOnly)}>{gapsOnly ? 'Show All' : 'Show'}</Text>
                                        </TouchableOpacity>
                                    </View>
                                )}
                                {gapCount === 0 && <Text style={styles.dateMeta}>{group.items.length} covered</Text>}
                            </View>
                            {visibleItems.map(taskInstance => <TaskCard key={taskInstance.id} taskInstance={taskInstance} showEvent fromScreen={fromScreen} />)}
                            {gapsOnly && group.items.length > visibleItems.length && (
                                <View style={styles.moreRow}>
                                    <Text style={styles.moreText}>{group.items.length - visibleItems.length} more task{group.items.length - visibleItems.length === 1 ? '' : 's'}</Text>
                                    <TouchableOpacity
                                        testID={`CoverageBoardShowAll-${group.key}`}
                                        accessibilityRole="button"
                                        accessibilityLabel={`Show all tasks for ${formatDateLabel(group.key)}`}
                                        onPress={() => setGapsOnlyGroups(prev => ({ ...prev, [group.key]: false }))}
                                        style={styles.moreButton}
                                    >
                                        <Text style={styles.moreButtonText}>Show All</Text>
                                    </TouchableOpacity>
                                </View>
                            )}
                        </View>
                    </View>
                );
            })}
            {hiddenGroupCount > 0 && (
                <TouchableOpacity
                    testID="CoverageBoardShowMore"
                    accessibilityRole="button"
                    accessibilityLabel={`Show ${hiddenTaskCount} more tasks across ${hiddenGroupCount} dates`}
                    onPress={() => setVisibleGroupCount(prev => prev + LOAD_MORE_GROUPS)}
                    style={styles.showMoreButton}
                >
                    <Text style={styles.showMoreText}>Show {hiddenTaskCount} more task{hiddenTaskCount === 1 ? '' : 's'}</Text>
                    <Unicon name="chevron-down-outline" size={16} colorVariant="tasks" />
                </TouchableOpacity>
            )}
        </View>
    );
}

const styles = StyleSheet.create(theme => ({
    board: { gap: 2 },
    group: { flexDirection: 'row' },
    dateRail: { width: 24, alignItems: 'center' },
    dateMarker: hasGap => ({ width: 11, height: 11, borderRadius: 6, marginTop: 7, backgroundColor: hasGap ? theme.colors.warning : theme.colors.tasks, boxShadow: `0px 0px 0px 4px ${hasGap ? theme.colors.warningContainer : theme.colors.tasksContainer}` }),
    railLine: { width: 2, flex: 1, minHeight: 24, backgroundColor: theme.colors.tasksContainer, marginTop: 5 },
    groupContent: { flex: 1, paddingBottom: 8 },
    groupHeader: { minHeight: 28, flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, paddingHorizontal: 5 },
    groupHeaderRight: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    dateLabel: { color: theme.colors.text, fontFamily: theme.fonts.bold, fontSize: 14 },
    dateMeta: { color: theme.colors.textSecondary, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 },
    gapToggle: active => ({ paddingHorizontal: 8, paddingVertical: 3, borderRadius: 7, backgroundColor: active ? theme.colors.warningContainer : theme.colors.surfaceVariant }),
    gapToggleText: active => ({ color: active ? theme.colors.warning : theme.colors.textSecondary, fontFamily: theme.fonts.bold, fontSize: 11 }),
    moreRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 5, paddingTop: 8 },
    moreText: { color: theme.colors.textSecondary, fontSize: 12 },
    moreButton: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 7, backgroundColor: theme.colors.tasksContainer },
    moreButtonText: { color: theme.colors.tasks, fontFamily: theme.fonts.bold, fontSize: 11 },
    showMoreButton: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 12, marginHorizontal: 5, backgroundColor: theme.colors.surface, borderRadius: 10, boxShadow: theme.shadows.card },
    showMoreText: { color: theme.colors.tasks, fontFamily: theme.fonts.bold, fontSize: 13 },
    empty: { alignItems: 'center', paddingVertical: 34, paddingHorizontal: 20, backgroundColor: theme.colors.surface, borderRadius: 14, boxShadow: theme.shadows.card },
    emptyIcon: { width: 44, height: 44, borderRadius: 22, backgroundColor: theme.colors.tasksContainer, alignItems: 'center', justifyContent: 'center', marginBottom: 10 },
    emptyTitle: { color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 16 },
    emptyText: { color: theme.colors.textSecondary, fontSize: 13, textAlign: 'center', marginTop: 4 },
}));
